import Header from "../../../components/common/header";
import LoadingSpinnerFull from "../../../components/common/loading-spinner/loading-spinner-full";
import { useState, useEffect } from "react";
import { HiOutlineXMark, HiOutlinePhoto } from "react-icons/hi2";
import { gql } from "../../../__generated__";
import { useQuery } from "@apollo/client";
import { Review as ReviewType } from "../../../__generated__/graphql";
import { useNavigate } from "react-router-dom";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import heroGray from "../../../assets/images/hero_gray.png";
import { LuShare } from "react-icons/lu";
import { toast } from "react-toastify";
import { useUser } from "../../../core/user-context";
import { isApp, isIOSApp } from "../../../utils/user-agent";
import { track } from "@amplitude/analytics-browser";

const GET_REVIEW_BY_USER = gql(`
  query GetReviewByUser {
    getReviewByUser {
      id
      store {
        id
        title
        address
      }
      content
      reviewImages {
        img
      }
      createdAt
      orderType
    }
  }
`);

const Review = () => {
  const [selectedReview, setSelectedReview] = useState<ReviewType | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { data, loading } = useQuery(GET_REVIEW_BY_USER);
  const navigate = useNavigate();
  const { user } = useUser();

  // 리뷰가 변경되면 첫 번째 이미지로 리셋
  useEffect(() => {
    setSelectedImageIndex(0);
  }, [selectedReview]);

  // 모든 이미지 미리 로드 (preload)
  useEffect(() => {
    if (
      selectedReview?.reviewImages &&
      selectedReview.reviewImages.length > 0
    ) {
      selectedReview.reviewImages.forEach((image: any) => {
        if (image?.img) {
          const img = new Image();
          img.src = image.img;
        }
      });
    }
  }, [selectedReview]);

  if (loading) {
    return <LoadingSpinnerFull />;
  }

  const copyMessageHasUser = 
`[${selectedReview?.store?.title}] ${selectedReview?.store?.address}
https://mahi.co.kr/store/${selectedReview?.store?.id}

────── ✦ ──────
${selectedReview?.content}
────── ✦ ──────

'${user?.name || "히어로"}'님이 직접 구매하고 쓴 찐후기에요!

가입 후 코드 입력하면 우리 둘 다 1OOO원 쿠폰을 받아요.

${user?.name || "히어로"}님의 추천인 코드: ${user?.referralCode}`; // prettier-ignore

  const handleShareClick = () => {
    track("내가 쓴 찐후기 공유하기 클릭", { 어디서: "내가 쓴 리뷰" });
    // 데스크톱에서는 클립보드 복사
    if (isApp() && isIOSApp()) {
      navigator
        .share({
          text: copyMessageHasUser,
        })
        .then(() => {
          toast.success("공유가 완료되었어요!");
        })
        .catch(() => {
          // 사용자가 공유를 취소한 경우
        });
    } else {
      navigator.clipboard
        .writeText(copyMessageHasUser)
        .then(() => {
          toast.success(
            <div style={{ whiteSpace: "pre-line" }}>
              <p>
                <span className="font-bold">
                  {selectedReview?.store?.title}
                </span>
                에 작성한 찐후기와
              </p>
              <p>
                <span className="font-bold">{user?.name || "히어로"}</span>님의
                추천인 코드가 복사되었어요.
              </p>
              <p>이제 친구에게 공유해보세요!</p>
            </div>,
          );
        })
        .catch(() => {
          toast.error("링크 복사에 실패했어요.");
        });
    }
  };

  return (
    <div className="w-full min-h-screen h-full bg-white overflow-y-scroll">
      <Header title="내가 쓴 리뷰" showBackButton />
      <div className="w-full p-4 space-y-4">
        <div className="bg-[#fd6180] border-2 border-[#fd6180]/20 rounded-2xl p-6 text-center shadow-lg relative overflow-hidden">
          <div className="absolute -right-6 -top-6 w-24 h-24 bg-white/10 rounded-full" />
          <div className="absolute -left-8 -bottom-8 w-32 h-32 bg-white/5 rounded-full" />
          <div className="relative">
            <div className="text-white text-lg font-bold">
              내가 작성한 찐후기를 클릭 후
              <br />
              <LuShare className="w-4 h-4 inline-block" /> 공유하기를 눌러
              <br />
              <span className="bg-white/20 px-3 py-1 rounded-lg mt-2 inline-block">
                친구를 초대하고 쿠폰을 받아보세요!
              </span>
              <br />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {data?.getReviewByUser.map((review: any) => (
            <div
              key={review.id}
              onClick={() => setSelectedReview(review)}
              className="aspect-square rounded-lg overflow-hidden relative cursor-pointer"
            >
              {review.reviewImages && review.reviewImages.length > 0 ? (
                <div className="relative w-full h-full">
                  <ResponsiveCDNImage
                    src={review.reviewImages[0]?.img}
                    alt="리뷰 이미지"
                    className="w-full h-full object-cover"
                    sizeRatio={0.5}
                  />
                </div>
              ) : (
                <div className="w-full h-full bg-gray-50 flex items-center justify-center">
                  <ResponsiveCDNImage
                    src={heroGray}
                    alt="리뷰 이미지"
                    className="w-full h-[50px] object-contain rounded-lg"
                    sizeRatio={0.25}
                  />
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                {review.reviewImages && review.reviewImages.length > 1 && (
                  <div className="inline-flex items-center gap-1 mb-1 bg-white/20 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium">
                    <HiOutlinePhoto className="w-3 h-3" />
                    <span>+{review.reviewImages.length - 1}</span>
                  </div>
                )}
                <div className="text-white font-medium mb-1">
                  {review.store?.title}
                </div>
                <div className="text-white/90 line-clamp-2">
                  {review.content}
                </div>
                <div className="text-white/70 text-md mt-1">
                  {new Date(review.createdAt).toLocaleDateString("ko-KR", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedReview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-[90%] max-w-[500px] max-h-[90vh] relative">
            <div className="p-6 overflow-y-auto max-h-[90vh]">
              <div className="flex justify-between items-start gap-2 mb-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex gap-1 items-center">
                      <div className="flex items-center gap-0.5 underline underline-offset-4 decoration-gray-600">
                        <span
                          onClick={() => {
                            navigate(`/store/${selectedReview.store.id}`);
                          }}
                          className="text-2xl font-semibold cursor-pointer"
                        >
                          {selectedReview.store.title}
                        </span>
                      </div>
                      <span className="font-medium text-gray-600">
                        {selectedReview.orderType === "PACK"
                          ? "예약구매"
                          : "실시간구매"}
                      </span>
                    </div>

                    <div
                      onClick={handleShareClick}
                      className="flex items-center gap-1 rounded-full bg-[#fd6180] text-white px-2 py-1 text-sm cursor-pointer"
                    >
                      <LuShare className="w-5 h-5" />
                      <span className="text-white text-md font-bold">
                        공유하기
                      </span>
                    </div>
                  </div>
                  <div className="text-gray-500">
                    {new Date(selectedReview.createdAt).toLocaleDateString(
                      "ko-KR",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      },
                    )}{" "}
                    {new Date(selectedReview.createdAt).toLocaleTimeString(
                      "ko-KR",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      },
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedReview(null);
                    setSelectedImageIndex(0);
                  }}
                  className="text-gray-500 active:text-gray-700"
                >
                  <HiOutlineXMark className="w-6 h-6" />
                </button>
              </div>

              {selectedReview.reviewImages &&
                selectedReview.reviewImages.length > 0 && (
                  <div className="mb-4">
                    {/* 대표 이미지 */}
                    <div className="mb-3">
                      <img
                        key={selectedImageIndex}
                        src={
                          selectedReview.reviewImages[selectedImageIndex]
                            ?.img || ""
                        }
                        alt={`리뷰 이미지 ${selectedImageIndex + 1}`}
                        className="w-full h-[300px] object-cover rounded-lg"
                        loading="eager"
                      />
                    </div>
                    {/* 썸네일 리스트 (여러 장일 때만 표시) */}
                    {selectedReview.reviewImages.length > 1 && (
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {selectedReview.reviewImages.map(
                          (image: any, index: number) => (
                            <button
                              key={index}
                              onClick={() => setSelectedImageIndex(index)}
                              className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                                selectedImageIndex === index
                                  ? "border-primary"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <img
                                src={image.img}
                                alt={`리뷰 이미지 썸네일 ${index + 1}`}
                                className="w-full h-full object-cover"
                                loading="eager"
                              />
                            </button>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                )}
              <div className="text-gray-700 whitespace-pre-wrap">
                {selectedReview.content}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Review;
